//src/pages/TikpagBIVariacaoBase.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "../axiosConfig";
import { ArrowLeft, BarChart3, RefreshCcw, Search, TrendingDown, TrendingUp } from "lucide-react";
import "./TikpagBIVariacao.css";

export default function TikpagBIVariacaoBase({ tipo }) {
  const navigate = useNavigate();
  const queda = tipo === "queda";
  const [filtros, setFiltros] = useState({
    dias: "30",
    busca: "",
    volume_minimo: "1000",
    qtd_minima: "1",
    ordenacao: "variacao_percentual",
    direcao: queda ? "asc" : "desc"
  });
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");

  const [paginaAtual, setPaginaAtual] = useState(1);
  const [porPagina, setPorPagina] = useState(50);

  const carregar = async (
    paginaDestino = paginaAtual,
    quantidadePorPagina = porPagina
  ) => {
    setLoading(true);
    setErro("");

    try {
      const { data } = await axios.get(
        "/admin/tikpag-bi/variacao-empresas",
        {
          params: {
            tipo,
            ...filtros,
            pagina: paginaDestino,
            por_pagina: quantidadePorPagina
          }
        }
      );

      setDados(data);

      setPaginaAtual(
        Number(
          data?.paginacao?.pagina_atual ||
          paginaDestino
        )
      );
    } catch (err) {
      setErro(
        err.response?.data?.error ||
          "Não foi possível carregar a análise das empresas."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
      setPaginaAtual(1);
      carregar(1, porPagina);
    }, [tipo]);

  const alterarFiltro = (campo, valor) => {
    setFiltros((anterior) => ({ ...anterior, [campo]: valor }));
  };

  const limparFiltros = async () => {
    const filtrosIniciais = {
      dias: "30",
      busca: "",
      volume_minimo: "1000",
      qtd_minima: "1",
      ordenacao: "variacao_percentual",
      direcao: queda ? "asc" : "desc"
    };

    setFiltros(filtrosIniciais);
    setPaginaAtual(1);
    setLoading(true);
    setErro("");

    try {
      const { data } = await axios.get(
        "/admin/tikpag-bi/variacao-empresas",
        {
          params: {
            tipo,
            ...filtrosIniciais,
            pagina: 1,
            por_pagina: porPagina
          }
        }
      );

      setDados(data);

      setPaginaAtual(
        Number(data?.paginacao?.pagina_atual || 1)
      );

    } catch (err) {
      setErro(
        err.response?.data?.error ||
          "Não foi possível limpar os filtros."
      );
    } finally {
      setLoading(false);
    }
  };

  const formatarMoeda = (valor) => Number(valor || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const formatarNumero = (valor) => Number(valor || 0).toLocaleString("pt-BR");
  const formatarPercentual = (valor) => {
    if (valor === null || valor === undefined) return "Nova";
    const numero = Number(valor || 0);
    return `${numero > 0 ? "+" : ""}${numero.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
  };

  const empresas = Array.isArray(dados?.empresas) ? dados.empresas : [];
  const topGrafico = useMemo(() => {
    const lista = empresas.filter((item) => item.variacao_percentual !== null).slice(0, 10);
    const maior = Math.max(1, ...lista.map((item) => Math.abs(Number(item.variacao_percentual || 0))));
    return lista.map((item) => ({ ...item, largura: (Math.abs(Number(item.variacao_percentual || 0)) / maior) * 100 }));
  }, [empresas]);

  const titulo = queda ? "Empresas em queda" : "Empresas em crescimento";
  const descricao = queda
    ? "Identifique empresas com retração no volume aprovado e priorize ações de recuperação."
    : "Identifique empresas que ampliaram o volume aprovado e acompanhe as melhores evoluções da carteira.";

  return (
    <div className={`tikpagBIVariacao-page ${queda ? "queda" : "crescimento"}`}>
      <header className="tikpagBIVariacao-header">
        <div>
          <span className="tikpagBIVariacao-kicker">BI Tikpag</span>
          <h1>{titulo}</h1>
          <p>{descricao}</p>
        </div>
        <button type="button" className="tikpagBIVariacao-back" onClick={() => navigate("/admin/tikpag-bi") }>
          <ArrowLeft size={16} /> Voltar ao BI
        </button>
      </header>

      {erro && <div className="tikpagBIVariacao-alert">{erro}</div>}

      <section className="tikpagBIVariacao-filters">
        <div className="tikpagBIVariacao-filterGrid">
          <div className="tikpagBIVariacao-field">
            <label>Período comparado</label>
            <select value={filtros.dias} onChange={(e) => alterarFiltro("dias", e.target.value)}>
              <option value="7">7 dias x 7 anteriores</option>
              <option value="15">15 dias x 15 anteriores</option>
              <option value="30">30 dias x 30 anteriores</option>
              <option value="60">60 dias x 60 anteriores</option>
              <option value="90">90 dias x 90 anteriores</option>
            </select>
          </div>
          <div className="tikpagBIVariacao-field">
            <label>Empresa ou CNPJ</label>
            <input value={filtros.busca} onChange={(e) => alterarFiltro("busca", e.target.value)} placeholder="Buscar na carteira" />
          </div>
          <div className="tikpagBIVariacao-field">
            <label>Volume mínimo</label>
            <input type="number" min="0" value={filtros.volume_minimo} onChange={(e) => alterarFiltro("volume_minimo", e.target.value)} />
          </div>
          <div className="tikpagBIVariacao-field">
            <label>Operações mínimas</label>
            <input type="number" min="0" value={filtros.qtd_minima} onChange={(e) => alterarFiltro("qtd_minima", e.target.value)} />
          </div>
          <div className="tikpagBIVariacao-field">
            <label>Ordenar por</label>
            <select value={filtros.ordenacao} onChange={(e) => alterarFiltro("ordenacao", e.target.value)}>
              <option value="variacao_percentual">Variação percentual</option>
              <option value="variacao_absoluta">Variação em reais</option>
              <option value="volume_atual">Volume atual</option>
              <option value="volume_anterior">Volume anterior</option>
              <option value="qtd_atual">Operações atuais</option>
              <option value="ticket_atual">Ticket atual</option>
            </select>
          </div>

          <div className="tikpagBIVariacao-field">
            <label>Itens por página</label>

            <select
              value={porPagina}
              onChange={(e) => {
                const quantidade = Number(e.target.value);

                setPorPagina(quantidade);
                setPaginaAtual(1);
                carregar(1, quantidade);
              }}
            >
              <option value={25}>25 empresas</option>
              <option value={50}>50 empresas</option>
              <option value={100}>100 empresas</option>
            </select>
          </div>

        </div>
        <div className="tikpagBIVariacao-actions">
          <button type="button" className="tikpagBIVariacao-button" onClick={limparFiltros} disabled={loading}>Limpar</button>
          <button type="button" className="tikpagBIVariacao-button primary" onClick={() => {
              setPaginaAtual(1);
              carregar(1, porPagina);
            }} disabled={loading}>
            {loading ? <RefreshCcw size={16} className="tikpagBIVariacao-spin" /> : <Search size={16} />} Aplicar filtros
          </button>
        </div>
      </section>

      <section className="tikpagBIVariacao-cards">
        <div className="tikpagBIVariacao-card"><span>Empresas encontradas</span><strong>{formatarNumero(dados?.resumo?.total_empresas)}</strong><small>após os filtros aplicados</small></div>
        <div className="tikpagBIVariacao-card"><span>Volume atual</span><strong>{formatarMoeda(dados?.resumo?.volume_atual)}</strong><small>período mais recente</small></div>
        <div className="tikpagBIVariacao-card"><span>Volume anterior</span><strong>{formatarMoeda(dados?.resumo?.volume_anterior)}</strong><small>janela comparativa anterior</small></div>
        <div className="tikpagBIVariacao-card"><span>Variação da seleção</span><strong>{formatarPercentual(dados?.resumo?.variacao_percentual_carteira)}</strong><small>diferença de {formatarMoeda(dados?.resumo?.variacao_absoluta)}</small></div>
      </section>

      <section className="tikpagBIVariacao-panel">
        <div className="tikpagBIVariacao-sectionHeader">
          <div>
            <h2>
            {queda ? (
              <TrendingDown size={18} />
            ) : (
              <TrendingUp size={18} />
            )}
            Destaques da página atual
          </h2>

          <p>
            As 10 maiores variações entre as empresas exibidas nesta página.
          </p>
          </div>
          <BarChart3 size={20} />
        </div>
        <div className="tikpagBIVariacao-chart">
          {topGrafico.map((item) => (
            <div className="tikpagBIVariacao-chartRow" key={item.imobiliaria_cnpj}>
              <div className="tikpagBIVariacao-chartLabel"><strong>{item.imobiliaria || "Sem nome"}</strong><span>{item.imobiliaria_cnpj}</span></div>
              <div className="tikpagBIVariacao-chartTrack"><div className="tikpagBIVariacao-chartBar" style={{ width: `${item.largura}%` }} /></div>
              <div className="tikpagBIVariacao-chartValue">{formatarPercentual(item.variacao_percentual)}</div>
            </div>
          ))}
          {!topGrafico.length && <div className="tikpagBIVariacao-empty">Nenhuma empresa disponível para o gráfico.</div>}
        </div>
      </section>

      <section className="tikpagBIVariacao-panel">
        <div className="tikpagBIVariacao-sectionHeader">
          <div><h2>Análise completa da carteira</h2><p>Volume, quantidade de operações, ticket médio e variações no período selecionado.</p></div>
          <span>
            {formatarNumero(
              dados?.paginacao?.total_registros
            )}{" "}
            empresas encontradas
          </span>
        </div>
        <div className="tikpagBIVariacao-tableWrap">
          <table className="tikpagBIVariacao-table">
            <thead><tr><th>Empresa</th><th>CNPJ</th><th>Volume atual</th><th>Volume anterior</th><th>Diferença</th><th>Variação</th><th>Ops. atuais</th><th>Ops. anteriores</th><th>Ticket atual</th><th>Ticket anterior</th><th>Ações</th></tr></thead>
            <tbody>
              {empresas.map((item) => (
                <tr key={item.imobiliaria_cnpj}>
                  <td><strong>{item.imobiliaria || "Sem nome"}</strong>{Number(item.empresa_nova || 0) === 1 && <span className="tikpagBIVariacao-new">Nova no período</span>}</td>
                  <td>{item.imobiliaria_cnpj || "-"}</td>
                  <td>{formatarMoeda(item.volume_atual)}</td>
                  <td>{formatarMoeda(item.volume_anterior)}</td>
                  <td>{formatarMoeda(item.variacao_absoluta)}</td>
                  <td><span className="tikpagBIVariacao-badge">{formatarPercentual(item.variacao_percentual)}</span></td>
                  <td>{formatarNumero(item.qtd_atual)}</td>
                  <td>{formatarNumero(item.qtd_anterior)}</td>
                  <td>{formatarMoeda(item.ticket_atual)}</td>
                  <td>{formatarMoeda(item.ticket_anterior)}</td>
                  <td><Link to={`/admin/tikpag-bi/imobiliaria/${item.imobiliaria_cnpj}`} className="tikpagBIVariacao-detail">Ver empresa</Link></td>
                </tr>
              ))}
              {!empresas.length && !loading && <tr><td colSpan="11" className="tikpagBIVariacao-empty">Nenhuma empresa encontrada com os filtros informados.</td></tr>}
              {loading && <tr><td colSpan="11" className="tikpagBIVariacao-empty">Carregando análise...</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="tikpagBIVariacao-pagination">
          <div className="tikpagBIVariacao-paginationInfo">
            Mostrando{" "}
            <strong>
              {formatarNumero(
                dados?.paginacao?.primeiro_registro
              )}
            </strong>{" "}
            até{" "}
            <strong>
              {formatarNumero(
                dados?.paginacao?.ultimo_registro
              )}
            </strong>{" "}
            de{" "}
            <strong>
              {formatarNumero(
                dados?.paginacao?.total_registros
              )}
            </strong>{" "}
            empresas
          </div>

          <div className="tikpagBIVariacao-paginationButtons">
            <button
              type="button"
              disabled={
                loading ||
                !dados?.paginacao?.tem_anterior
              }
              onClick={async () => {
                await carregar(
                  Number(paginaAtual) - 1,
                  porPagina
                );

                document
                  .querySelector(".tikpagBIVariacao-tableWrap")
                  ?.scrollIntoView({
                    behavior: "smooth",
                    block: "start"
                  });
              }}
            >
              Anterior
            </button>

            <span>
              Página{" "}
              <strong>
                {formatarNumero(
                  dados?.paginacao?.pagina_atual || 1
                )}
              </strong>{" "}
              de{" "}
              <strong>
                {formatarNumero(
                  dados?.paginacao?.total_paginas || 1
                )}
              </strong>
            </span>

            <button
              type="button"
              disabled={
                loading ||
                !dados?.paginacao?.tem_proxima
              }
              onClick={async () => {
                await carregar(
                  Number(paginaAtual) + 1,
                  porPagina
                );

                document
                  .querySelector(".tikpagBIVariacao-tableWrap")
                  ?.scrollIntoView({
                    behavior: "smooth",
                    block: "start"
                  });
              }}
            >
              Próxima
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}